import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import {
    ArrowLeft,
    Check,
    ExternalLink,
    Loader2,
    Terminal,
} from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { cn } from '@apps/web/lib/utils';
import { grokGoldAuditMetadata } from '@apps/web/routes/_app/admin/voice-evals/catalog_audit';
import {
    formatDateTime,
    shortSha,
} from '@apps/web/routes/_app/admin/voice-evals/shared';
import { useVoiceEvalCatalog } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type EvalCatalogCase } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/voice-evals/catalog')({
    head: () => ({
        meta: [{ title: 'Voice Eval Catalog | Phoebe Admin' }],
    }),
    component: VoiceEvalCatalogPage,
});

// Section order and labels mirror the harness layers: full-pipeline
// e2e behavior first, then the post-call QA judge floor, then synthetic
// prompt-only tests. Historical snapshots used `legacy_text_simulation`;
// grouping it here preserves their visibility without overstating coverage.
const KIND_SECTIONS: ReadonlyArray<{
    readonly kind: string;
    readonly label: string;
}> = [
    { kind: 'agent_behavior', label: 'Agent behavior — e2e pipeline' },
    { kind: 'post_call_qa_judge', label: 'Post-call QA judge' },
    {
        kind: 'prompt_only',
        label: 'Prompt/tool behavior — no voice pipeline',
    },
];

/**
 * The submodule file within the use-case directory, so the catalog
 * reads like the eval file tree (e.g. `replay`, `qa_critic`).
 */
function moduleFileName(catalogCase: EvalCatalogCase): string {
    const prefix = `.${catalogCase.use_case}.`;
    const index = catalogCase.module.lastIndexOf(prefix);
    if (index >= 0) {
        return catalogCase.module.slice(index + prefix.length);
    }
    return catalogCase.module.split('.').pop() ?? catalogCase.module;
}

function kindSections(cases: ReadonlyArray<EvalCatalogCase>) {
    const known = new Set(KIND_SECTIONS.map((section) => section.kind));
    known.add('legacy_text_simulation');
    const sections = KIND_SECTIONS.map((section) => ({
        ...section,
        cases: cases.filter(
            (catalogCase) =>
                catalogCase.kind === section.kind ||
                (section.kind === 'prompt_only' &&
                    catalogCase.kind === 'legacy_text_simulation'),
        ),
    }));
    const other = cases.filter(
        (catalogCase) => !catalogCase.kind || !known.has(catalogCase.kind),
    );
    if (other.length > 0) {
        sections.push({ kind: 'other', label: 'Other', cases: other });
    }
    return sections.filter((section) => section.cases.length > 0);
}

/** `--case-filter` is a regex; anchor an exact-name match safely. */
function escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Copies the exact local CLI invocation for one case. Finished runs
 * auto-upload in development, so copy → run in a terminal → refresh is
 * the local loop until run-from-UI orchestration exists.
 */
function CopyRunCommandButton({
    catalogCase,
}: {
    catalogCase: EvalCatalogCase;
}) {
    const [copied, setCopied] = useState(false);
    const command =
        'bazel run //evals:cloud -- run ' +
        `${catalogCase.module} ` +
        `--case-filter '^${escapeRegex(catalogCase.case_name)}$'`;
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => {
                        void navigator.clipboard.writeText(command);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1500);
                    }}
                >
                    {copied ? (
                        <Check className="h-3 w-3 text-green-700" />
                    ) : (
                        <Terminal className="text-muted-foreground h-3 w-3" />
                    )}
                </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-md">
                <span className="font-mono text-xs">{command}</span>
            </TooltipContent>
        </Tooltip>
    );
}

function GrokGoldSuiteAudit({
    cases,
}: {
    cases: ReadonlyArray<EvalCatalogCase>;
}) {
    const rows = cases.flatMap((catalogCase) => {
        const audit = grokGoldAuditMetadata(catalogCase);
        return audit ? [{ catalogCase, audit }] : [];
    });
    const [firstRow] = rows;
    if (!firstRow) {
        return null;
    }
    const ported = rows.filter(
        ({ audit }) => audit.origin === 'ported_normal_voice',
    ).length;
    const defaultBoundary = firstRow.audit.harnessBoundary;
    return (
        <div className="space-y-2 rounded-md border border-purple-200 bg-purple-50/40 p-3">
            <div>
                <div className="text-sm font-medium">
                    Grok gold suite audit · {rows.length} reviewed cases
                </div>
                <p className="text-muted-foreground text-xs">
                    {ported} deliberately ported from normal voice ·{' '}
                    {rows.length - ported} retained Grok call-control cases.
                    Every row below names its precedent and observable pass
                    contract.
                </p>
                <p className="mt-1 text-xs text-amber-800">
                    Harness boundary: {defaultBoundary}
                </p>
                <p className="mt-1 text-xs text-amber-800">
                    Deliberately kept in their dedicated suites: noisy audio,
                    crosstalk, acoustic interruption, voicemail detection,
                    preference persistence, and post-call extraction.
                </p>
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Gold case</TableHead>
                        <TableHead>Source precedent</TableHead>
                        <TableHead>Why this is high signal</TableHead>
                        <TableHead>Observable pass contract</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map(({ catalogCase, audit }) => (
                        <TableRow key={`audit-${catalogCase.registry_id}`}>
                            <TableCell className="align-top">
                                <div className="font-mono text-xs">
                                    {catalogCase.source_case_id ??
                                        catalogCase.case_name}
                                </div>
                                <Badge variant="outline" className="mt-1">
                                    {audit.origin === 'ported_normal_voice'
                                        ? 'ported'
                                        : 'retained'}
                                </Badge>
                            </TableCell>
                            <TableCell className="align-top">
                                <div className="space-y-1">
                                    {audit.sourceCaseIds.map((source) => (
                                        <div
                                            key={source}
                                            className="font-mono text-xs"
                                        >
                                            {source}
                                        </div>
                                    ))}
                                </div>
                            </TableCell>
                            <TableCell className="max-w-sm align-top text-xs">
                                <div>{audit.selectionRationale}</div>
                                <div className="mt-1 text-red-800">
                                    Risk: {audit.risk}
                                </div>
                            </TableCell>
                            <TableCell className="max-w-sm align-top text-xs">
                                <ul className="list-disc space-y-1 pl-4">
                                    {audit.observablePassCriteria.map(
                                        (criterion) => (
                                            <li key={criterion}>{criterion}</li>
                                        ),
                                    )}
                                </ul>
                                {audit.harnessBoundary !== defaultBoundary && (
                                    <p className="mt-2 text-amber-800">
                                        Case boundary: {audit.harnessBoundary}
                                    </p>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function LastResultBadge({ catalogCase }: { catalogCase: EvalCatalogCase }) {
    const { recent } = catalogCase;
    if (recent.last_success === null || recent.last_success === undefined) {
        return (
            <Badge variant="outline" className="text-muted-foreground">
                never run
            </Badge>
        );
    }
    if (recent.last_success) {
        return (
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                PASS
            </Badge>
        );
    }
    return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">FAIL</Badge>
    );
}

function RecentResultsCell({ catalogCase }: { catalogCase: EvalCatalogCase }) {
    const { recent } = catalogCase;
    if (recent.results === 0) {
        return <span className="text-muted-foreground text-xs">—</span>;
    }
    const allPassed = recent.passed === recent.results;
    return (
        <span
            className={cn(
                'text-xs',
                allPassed ? 'text-muted-foreground' : 'text-red-700',
            )}
        >
            {recent.passed}/{recent.results} passed
            {recent.flaky > 0 && ` · ${recent.flaky} flaky`}
        </span>
    );
}

function VoiceEvalCatalogPage() {
    const { data, isLoading } = useVoiceEvalCatalog();

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
        );
    }

    const useCases = data?.use_cases ?? [];
    const sourceRun = data?.source_run ?? null;
    const totalCases = useCases.reduce(
        (sum, useCase) => sum + useCase.cases.length,
        0,
    );

    return (
        <div className="flex h-full flex-col gap-4 overflow-auto p-4">
            <div>
                <Button variant="ghost" size="sm" asChild={true}>
                    <Link to="/admin/voice-evals">
                        <ArrowLeft className="mr-1 h-4 w-4" />
                        Runs
                    </Link>
                </Button>
            </div>
            <div>
                <h1 className="text-lg font-semibold">Voice Eval Catalog</h1>
                <p className="text-muted-foreground text-sm">
                    {totalCases} cases across {useCases.length} use cases,
                    merged from the registries of recent runs
                    {sourceRun && (
                        <Link
                            to="/admin/voice-evals/runs/$runId"
                            params={{ runId: sourceRun.id }}
                            className="ml-1 underline underline-offset-2"
                        >
                            (latest: {sourceRun.git_branch ?? 'unknown'}@
                            {shortSha(sourceRun.git_commit)} ·{' '}
                            {formatDateTime(
                                sourceRun.finished_at ?? sourceRun.created_at,
                            )}
                            )
                        </Link>
                    )}
                    . Recent results cover the last{' '}
                    {data?.history_window_days ?? 30} days.
                </p>
            </div>
            {useCases.length === 0 && (
                <div className="text-muted-foreground py-12 text-center text-sm">
                    No registry snapshot yet — the catalog fills in after the
                    first uploaded eval run.
                </div>
            )}
            {useCases.length > 0 && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                            Coverage by use case
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Use case</TableHead>
                                    <TableHead className="text-right">
                                        Agent behavior
                                    </TableHead>
                                    <TableHead className="text-right">
                                        QA judge
                                    </TableHead>
                                    <TableHead className="text-right">
                                        Prompt only
                                    </TableHead>
                                    <TableHead className="text-right">
                                        Prod pipeline
                                    </TableHead>
                                    <TableHead className="text-right">
                                        Recent results
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {useCases.map((useCase) => {
                                    const results = useCase.cases.reduce(
                                        (sum, catalogCase) =>
                                            sum + catalogCase.recent.results,
                                        0,
                                    );
                                    const passed = useCase.cases.reduce(
                                        (sum, catalogCase) =>
                                            sum + catalogCase.recent.passed,
                                        0,
                                    );
                                    return (
                                        <TableRow key={useCase.use_case}>
                                            <TableCell className="font-mono text-xs">
                                                <a
                                                    href={`#${useCase.use_case}`}
                                                    className="underline-offset-2 hover:underline"
                                                >
                                                    {useCase.use_case}
                                                </a>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {useCase.agent_behavior_count ||
                                                    '—'}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {useCase.qa_judge_count || '—'}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {useCase.prompt_only_count ||
                                                    '—'}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {useCase.prod_pipeline_count ||
                                                    '—'}
                                            </TableCell>
                                            <TableCell
                                                className={cn(
                                                    'text-right text-xs',
                                                    results > 0 &&
                                                        passed < results
                                                        ? 'text-red-700'
                                                        : 'text-muted-foreground',
                                                )}
                                            >
                                                {results > 0
                                                    ? `${passed}/${results} passed`
                                                    : 'no recent runs'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
            {useCases.map((useCase) => (
                <Card key={useCase.use_case} id={useCase.use_case}>
                    <CardHeader className="pb-2">
                        <CardTitle className="font-mono text-sm font-medium">
                            {useCase.use_case}
                            <span className="text-muted-foreground ml-2 font-sans font-normal">
                                {useCase.cases.length} cases
                            </span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {useCase.use_case === 'grok_realtime' && (
                            <GrokGoldSuiteAudit cases={useCase.cases} />
                        )}
                        {kindSections(useCase.cases).map((section) => (
                            <div key={section.kind}>
                                <div className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                                    {section.label}
                                    <span className="ml-1 normal-case">
                                        · {section.cases.length}
                                    </span>
                                </div>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Last result</TableHead>
                                            <TableHead>Case</TableHead>
                                            <TableHead>File</TableHead>
                                            <TableHead>Harness</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Recent (30d)</TableHead>
                                            <TableHead>Last run</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {section.cases.map((catalogCase) => (
                                            <TableRow
                                                key={catalogCase.registry_id}
                                            >
                                                <TableCell>
                                                    <LastResultBadge
                                                        catalogCase={
                                                            catalogCase
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Link
                                                        to="/admin/voice-evals/cases"
                                                        search={{
                                                            registry_id:
                                                                catalogCase.registry_id,
                                                        }}
                                                        className="font-mono text-xs underline-offset-2 hover:underline"
                                                    >
                                                        {catalogCase.case_name}
                                                    </Link>
                                                    {catalogCase.production_source_url && (
                                                        <a
                                                            href={
                                                                catalogCase.production_source_url
                                                            }
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-muted-foreground ml-1 inline-flex align-middle"
                                                        >
                                                            <ExternalLink className="h-3 w-3" />
                                                        </a>
                                                    )}
                                                    <span className="ml-1 inline-flex align-middle">
                                                        <CopyRunCommandButton
                                                            catalogCase={
                                                                catalogCase
                                                            }
                                                        />
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground font-mono text-xs">
                                                    {moduleFileName(
                                                        catalogCase,
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-muted-foreground text-xs">
                                                    {catalogCase.harness ?? '—'}
                                                </TableCell>
                                                <TableCell>
                                                    {catalogCase.status && (
                                                        <Badge
                                                            variant="outline"
                                                            className={
                                                                catalogCase.status ===
                                                                'known_gap'
                                                                    ? 'text-amber-700'
                                                                    : 'text-blue-700'
                                                            }
                                                        >
                                                            {catalogCase.status}
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <RecentResultsCell
                                                        catalogCase={
                                                            catalogCase
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                                                    {catalogCase.recent
                                                        .last_result_at
                                                        ? formatDistanceToNowStrict(
                                                              new Date(
                                                                  catalogCase
                                                                      .recent
                                                                      .last_result_at,
                                                              ),
                                                              {
                                                                  addSuffix: true,
                                                              },
                                                          )
                                                        : '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
